import { TbTrash } from 'react-icons/tb';
import {
    AccordionItem,
    AccordionPanel,
    Stack,
    Box,
    Checkbox,
    Flex,
    IconButton,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from '@chakra-ui/react';
import { Button, Input, LoadingIndicator, Tooltip } from '@/components/v4';
import { textBoxStyling } from 'components/forms/new/formStyles';
import RingGroupAccordionSectionButton from './RingGroupAccordionSectionButton';

export default function RingGroupMembersSection({
    memberSearch,
    setMemberSearch,
    isLoadingMembers,
    isLoadingCandidates,
    hasEligibleMembersWithPhoneNumber,
    allCheckboxRowsChecked,
    someCheckboxRowsChecked,
    checkboxRows,
    toggleAllPending,
    isSubmitting,
    memberRows,
    isSearchResultEmpty,
    memberPage,
    totalMemberPages,
    totalMemberCount,
    memberRangeStart,
    memberRangeEnd,
    onPrevMemberPage,
    onNextMemberPage,
    toggleMemberPending,
    setVoicemailOverride,
    setDeleteConfirmId,
    darkMode,
    panelBg,
    accordionBorderColor,
    colorScheme,
    pendingChangeCount,
    onClearAllPendingChanges,
}) {
    const scheme = colorScheme || 'primary';
    const rowHighlightBg = darkMode ? `${scheme}.900` : `${scheme}.50`;
    const pendingBarBg = darkMode ? `${scheme}.900` : `${scheme}.50`;
    return (
        <AccordionItem
            borderColor={accordionBorderColor}
            mb={2}
            borderWidth="1px"
            borderRadius="md"
            overflow="hidden">
            <RingGroupAccordionSectionButton darkMode={darkMode}>
                Members
            </RingGroupAccordionSectionButton>
            <AccordionPanel px={4} py={4} bg={panelBg}>
                {isLoadingMembers || isLoadingCandidates ? (
                    <Flex
                        align="center"
                        justify="center"
                        minH="120px"
                        data-testid="members-loading-indicator">
                        <LoadingIndicator sx={{ transform: 'scale(2)' }} />
                    </Flex>
                ) : !hasEligibleMembersWithPhoneNumber ? (
                    <Text fontSize="sm" color="gray.500">
                        No members are available
                    </Text>
                ) : (
                    <Stack spacing={3}>
                        <Input
                            placeholder="Search by name or number..."
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            sx={textBoxStyling}
                        />

                        {pendingChangeCount > 0 && (
                            <Flex
                                align="center"
                                justify="space-between"
                                bg={pendingBarBg}
                                borderRadius="md"
                                px={3}
                                py={2}>
                                <Text fontSize="sm" fontWeight="semibold">
                                    {pendingChangeCount} pending change
                                    {pendingChangeCount === 1 ? '' : 's'}
                                </Text>
                                <Button
                                    variant="link"
                                    size="sm"
                                    isDisabled={isSubmitting}
                                    onClick={onClearAllPendingChanges}>
                                    Clear All
                                </Button>
                            </Flex>
                        )}

                        <Box
                            borderWidth="1px"
                            borderColor={accordionBorderColor}
                            borderRadius="md"
                            overflow="hidden"
                            maxH="320px"
                            overflowY="auto">
                            <Table size="sm">
                                <Thead>
                                    <Tr
                                        sx={{
                                            th: { textTransform: 'none' },
                                        }}>
                                        <Th>Name</Th>
                                        <Th>Phone Number</Th>
                                        <Th>Extension</Th>
                                        <Th>Voicemail</Th>
                                        <Th>
                                            <Flex
                                                justify="center"
                                                align="center">
                                                <Checkbox
                                                    verticalAlign="middle"
                                                    mb={0}
                                                    colorScheme={scheme}
                                                    isChecked={
                                                        allCheckboxRowsChecked
                                                    }
                                                    isIndeterminate={
                                                        someCheckboxRowsChecked &&
                                                        !allCheckboxRowsChecked
                                                    }
                                                    isDisabled={
                                                        checkboxRows.length ===
                                                            0 || isSubmitting
                                                    }
                                                    onChange={toggleAllPending}
                                                />
                                            </Flex>
                                        </Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {isSearchResultEmpty ? (
                                        <Tr>
                                            <Td colSpan={5}>
                                                <Text
                                                    fontSize="sm"
                                                    color="gray.500">
                                                    No eligible 8x8 users found
                                                </Text>
                                            </Td>
                                        </Tr>
                                    ) : (
                                        memberRows.map((row) => {
                                            const showActionMenu =
                                                row.committedIsMember &&
                                                !row.isPending;
                                            const isRowClickable =
                                                !showActionMenu;
                                            return (
                                                <Tr
                                                    key={row.id}
                                                    onClick={
                                                        isRowClickable
                                                            ? () =>
                                                                  toggleMemberPending(
                                                                      row.id,
                                                                  )
                                                            : undefined
                                                    }
                                                    cursor={
                                                        isRowClickable
                                                            ? 'pointer'
                                                            : 'default'
                                                    }
                                                    bg={
                                                        row.isMember
                                                            ? rowHighlightBg
                                                            : undefined
                                                    }>
                                                    <Td>{row.name}</Td>
                                                    <Td>{row.phoneNumber}</Td>
                                                    <Td>{row.extension}</Td>
                                                    <Td>
                                                        {showActionMenu ? (
                                                            <Checkbox
                                                                verticalAlign="middle"
                                                                mb={0}
                                                                colorScheme={
                                                                    scheme
                                                                }
                                                                isChecked={
                                                                    row.voicemailAccess
                                                                }
                                                                isDisabled={
                                                                    isSubmitting
                                                                }
                                                                onChange={(e) =>
                                                                    setVoicemailOverride(
                                                                        row.id,
                                                                        e.target
                                                                            .checked,
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </Td>
                                                    <Td
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }>
                                                        <Flex
                                                            justify="center"
                                                            align="center">
                                                            {showActionMenu ? (
                                                                <Tooltip label="Delete">
                                                                    <IconButton
                                                                        icon={
                                                                            <TbTrash size="20px" />
                                                                        }
                                                                        aria-label="Delete"
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        colorScheme="red"
                                                                        isDisabled={
                                                                            isSubmitting
                                                                        }
                                                                        onClick={() =>
                                                                            setDeleteConfirmId(
                                                                                row.id,
                                                                            )
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            ) : (
                                                                <Checkbox
                                                                    verticalAlign="middle"
                                                                    mb={0}
                                                                    colorScheme={
                                                                        scheme
                                                                    }
                                                                    isChecked={
                                                                        row.isMember
                                                                    }
                                                                    isDisabled={
                                                                        isSubmitting
                                                                    }
                                                                    onChange={() =>
                                                                        toggleMemberPending(
                                                                            row.id,
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        </Flex>
                                                    </Td>
                                                </Tr>
                                            );
                                        })
                                    )}
                                </Tbody>
                            </Table>
                        </Box>

                        {!isSearchResultEmpty && (
                            <Flex
                                justify="space-between"
                                align="center"
                                gap={3}
                                wrap="wrap">
                                <Text fontSize="xs" color="gray.500">
                                    Showing {memberRangeStart}-{memberRangeEnd}{' '}
                                    of {totalMemberCount} members
                                </Text>
                                <Flex align="center" gap={3}>
                                    <Button
                                        variant="outline"
                                        size="xs"
                                        onClick={onPrevMemberPage}
                                        isDisabled={memberPage <= 1}>
                                        Previous
                                    </Button>
                                    <Text fontSize="xs" color="gray.500">
                                        Page {memberPage} of {totalMemberPages}
                                    </Text>
                                    <Button
                                        variant="outline"
                                        size="xs"
                                        onClick={onNextMemberPage}
                                        isDisabled={
                                            memberPage >= totalMemberPages
                                        }>
                                        Next
                                    </Button>
                                </Flex>
                            </Flex>
                        )}
                    </Stack>
                )}
            </AccordionPanel>
        </AccordionItem>
    );
}
